import { useTranslation } from 'react-i18next';
import Flag from 'react-world-flags';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CustomLanguageDropdown from './customLanguageDropdown';
import '../css/languageHandler.scss';

function getCookie(name: string) {
  if (typeof document === 'undefined') return 'en';
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

export default function LanguageHandler() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [locale, setLocale] = useState<string>('en');

  useEffect(() => {
    const cookie = getCookie('locale');
    if (cookie) {
      setLocale(cookie);
      i18n.changeLanguage(cookie);
    }
  }, [i18n]);

  const handleChange = async (selectedLocale: string) => {
    try {
      setLocale(selectedLocale);
      i18n.changeLanguage(selectedLocale);
      document.cookie = `locale=${selectedLocale}; path=/; max-age=31536000`;
    } catch (err) {
      console.error('Failed to change language:', err);
    }
  };

  const options = [
    {
      value: 'en',
      label: (
        <span className="dropdown-option">
          <Flag code="GB" className="flag-small" />
          {t('languages.en')}
        </span>
      ),
    },
    {
      value: 'lt',
      label: (
        <span className="dropdown-option">
          <Flag code="LT" className="flag-small" />
          {t('languages.lt')}
        </span>
      ),
    },
    {
      value: 'it',
      label: (
        <span className="dropdown-option">
          <Flag code="IT" className="flag-small" />
          {t('languages.it')}
        </span>
      ),
    },
    {
      value: 'ro',
      label: (
        <span className="dropdown-option">
          <Flag code="RO" className="flag-small" />
          {t('languages.ro')}
        </span>
      ),
    },
    {
      value: 'fr',
      label: (
        <span className="dropdown-option">
          <Flag code="FR" className="flag-small" />
          {t('languages.fr')}
        </span>
      ),
    },
    {
      value: 'de',
      label: (
        <span className="dropdown-option">
          <Flag code="DE" className="flag-small" />
          {t('languages.de')}
        </span>
      ),
    },
    {
      value: 'es',
      label: (
        <span className="dropdown-option">
          <Flag code="ES" className="flag-small" />
          {t('languages.es')}
        </span>
      ),
    },
    {
      value: 'nl',
      label: (
        <span className="dropdown-option">
          <Flag code="NL" className="flag-small" />
          {t('languages.nl')}
        </span>
      ),
    },
    {
      value: 'al',
      label: (
        <span className="dropdown-option">
          <Flag code="AL" className="flag-small" />
          {t('languages.al')}
        </span>
      ),
    },
    {
      value: 'pt',
      label: (
        <span className="dropdown-option">
          <Flag code="PT" className="flag-small" />
          {t('languages.pt')}
        </span>
      ),
    },
    {
      value: 'el',
      label: (
        <span className="dropdown-option">
          <Flag code="GR" className="flag-small" />
          {t('languages.el')}
        </span>
      ),
    },
    {
      value: 'pl',
      label: (
        <span className="dropdown-option">
          <Flag code="PL" className="flag-small" />
          {t('languages.pl')}
        </span>
      ),
    },
    {
      value: 'et',
      label: (
        <span className="dropdown-option">
          <Flag code="EE" className="flag-small" />
          {t('languages.et')}
        </span>
      ),
    },
    {
      value: 'ca',
      label: (
        <span className="dropdown-option">
          <Flag code="ES" className="flag-small" />
          {t('languages.ca')}
        </span>
      ),
    },
    {
      value: 'bs',
      label: (
        <span className="dropdown-option">
          <Flag code="BA" className="flag-small" />
          {t('languages.bs')}
        </span>
      ),
    },
    {
      value: 'da',
      label: (
        <span className="dropdown-option">
          <Flag code="DK" className="flag-small" />
          {t('languages.da')}
        </span>
      ),
    },
    {
      value: 'hr',
      label: (
        <span className="dropdown-option">
          <Flag code="HR" className="flag-small" />
          {t('languages.hr')}
        </span>
      ),
    },
    {
      value: 'lv',
      label: (
        <span className="dropdown-option">
          <Flag code="LV" className="flag-small" />
          {t('languages.lv')}
        </span>
      ),
    },
    {
      value: 'hu',
      label: (
        <span className="dropdown-option">
          <Flag code="HU" className="flag-small" />
          {t('languages.hu')}
        </span>
      ),
    },
    {
      value: 'no',
      label: (
        <span className="dropdown-option">
          <Flag code="NO" className="flag-small" />
          {t('languages.no')}
        </span>
      ),
    },
    {
      value: 'pt-br',
      label: (
        <span className="dropdown-option">
          <Flag code="BR" className="flag-small" />
          {t('languages.pt-br')}
        </span>
      ),
    },
    {
      value: 'sl',
      label: (
        <span className="dropdown-option">
          <Flag code="SI" className="flag-small" />
          {t('languages.sl')}
        </span>
      ),
    },
    {
      value: 'fi',
      label: (
        <span className="dropdown-option">
          <Flag code="FI" className="flag-small" />
          {t('languages.fi')}
        </span>
      ),
    },
    {
      value: 'sv',
      label: (
        <span className="dropdown-option">
          <Flag code="SE" className="flag-small" />
          {t('languages.sv')}
        </span>
      ),
    },
    {
      value: 'tr',
      label: (
        <span className="dropdown-option">
          <Flag code="TR" className="flag-small" />
          {t('languages.tr')}
        </span>
      ),
    },
    {
      value: 'is',
      label: (
        <span className="dropdown-option">
          <Flag code="IS" className="flag-small" />
          {t('languages.is')}
        </span>
      ),
    },
    {
      value: 'cs',
      label: (
        <span className="dropdown-option">
          <Flag code="CZ" className="flag-small" />
          {t('languages.cs')}
        </span>
      ),
    },
    {
      value: 'bg',
      label: (
        <span className="dropdown-option">
          <Flag code="BG" className="flag-small" />
          {t('languages.bg')}
        </span>
      ),
    },
    {
      value: 'mk',
      label: (
        <span className="dropdown-option">
          <Flag code="MK" className="flag-small" />
          {t('languages.mk')}
        </span>
      ),
    },
    {
      value: 'ru',
      label: (
        <span className="dropdown-option">
          <Flag code="RU" className="flag-small" />
          {t('languages.ru')}
        </span>
      ),
    },
    {
      value: 'se',
      label: (
        <span className="dropdown-option">
          <Flag code="SE" className="flag-small" />
          {t('languages.se')}
        </span>
      ),
    },
    {
      value: 'uk',
      label: (
        <span className="dropdown-option">
          <Flag code="UA" className="flag-small" />
          {t('languages.uk')}
        </span>
      ),
    },
    {
      value: 'hy',
      label: (
        <span className="dropdown-option">
          <Flag code="AM" className="flag-small" />
          {t('languages.hy')}
        </span>
      ),
    },
    {
      value: 'he',
      label: (
        <span className="dropdown-option">
          <Flag code="IL" className="flag-small" />
          {t('languages.he')}
        </span>
      ),
    },
    {
      value: 'ar',
      label: (
        <span className="dropdown-option">
          <Flag code="SA" className="flag-small" />
          {t('languages.ar')}
        </span>
      ),
    },
    {
      value: 'ka',
      label: (
        <span className="dropdown-option">
          <Flag code="GE" className="flag-small" />
          {t('languages.ka')}
        </span>
      ),
    },
  ];

  const flagMap: Record<string, string> = {
    en: 'GB',
    lt: 'LT',
    it: 'IT',
    ro: 'RO',
    fr: 'FR',
    de: 'DE',
    es: 'ES',
    nl: 'NL',
    al: 'AL',
    pt: 'PT',
    el: 'GR',
    pl: 'PL',
    et: 'EE',
    ca: 'ES',
    bs: 'BA',
    da: 'DK',
    hr: 'HR',
    lv: 'LV',
    hu: 'HU',
    no: 'NO',
    'pt-br': 'BR',
    sl: 'SI',
    fi: 'FI',
    sv: 'SE',
    tr: 'TR',
    is: 'IS',
    cs: 'CZ',
    bg: 'BG',
    mk: 'MK',
    ru: 'RU',
    se: 'SE',
    uk: 'UA',
    hy: 'AM',
    he: 'IL',
    ar: 'SA',
    ka: 'GE',
  };

  return <CustomLanguageDropdown options={options} value={locale} onChange={handleChange} className="language-dropdown" renderValue={(val: string) => <Flag code={flagMap[val]} className="flag-large" />} />;
}
